import { useMemo } from 'react';
import type { TreeData, TreeLink, TreeNode } from '@/api/tree.api';

// ─── Konstanten ──────────────────────────────────────────────────────────────

const NODE_W       = 160;
const NODE_H       = 90;
const SYMBOL_W     = 40;
const PARTNER_GAP  = 20;
const SIBLING_GAP  = 60;
const FAMILY_GAP   = 100;
const V_STEP       = 230;
const HALF_COUPLE  = SYMBOL_W / 2 + PARTNER_GAP + NODE_W / 2; // 120 → Paar-Mitte-zu-Mitte = 240

// ─── Farbpalette für Familienlinien (Geschwister-Gruppen) ────────────────────

export const FAMILY_COLORS = [
  '#3b82f6', // Blau
  '#10b981', // Smaragd
  '#f59e0b', // Bernstein
  '#ef4444', // Rot
  '#8b5cf6', // Violett
  '#06b6d4', // Cyan
  '#f97316', // Orange
  '#ec4899', // Pink
  '#14b8a6', // Teal
  '#6366f1', // Indigo
  '#84cc16', // Limette
  '#a855f7', // Lila
];

// ─── Typen ───────────────────────────────────────────────────────────────────

interface FamilyUnit {
  id: string;
  p1Id: string;
  p2Id: string | null;
  link: TreeLink | null;
  childIds: string[];
}

/** Starre Partner-Kette einer Ebene (feste interne Abstände, frei verschiebbar). */
interface LayoutBlock {
  rank: number;
  members: string[];
  offset: Map<string, number>; // Mitglied → Versatz relativ zur Blockmitte
  x: number;                    // Blockmitte (frei verschiebbar)
  minO: number;                 // kleinster Versatz (linker Rand)
  maxO: number;                 // größter Versatz (rechter Rand)
  order: number;                // ursprüngliche Reihenfolge (Stabilität)
}

export interface LayoutPerson {
  x: number;
  y: number;
  data: TreeNode;
}

export interface LayoutCoupleSymbol {
  x: number;
  y: number;
  link: TreeLink | null;
}

export interface LayoutFamilyLine {
  symbolX: number;
  junctionY: number;
  parentY: number;
  isCouple: boolean;
  childPositions: Array<{ x: number; y: number }>;
  colorIndex: number;
}

export interface FamilyTreeLayoutResult {
  layoutPersons: LayoutPerson[];
  layoutSymbols: LayoutCoupleSymbol[];
  layoutLines: LayoutFamilyLine[];
}

// ─── Hilfsfunktionen ─────────────────────────────────────────────────────────

const getSortYear = (dateStr?: string | null): number => {
  if (!dateStr) return 9999;
  const match = dateStr.match(/\d{4}/);
  return match ? parseInt(match[0], 10) : 9999;
};

/** Jahr aus einem (ISO-)Datum; fehlt das Datum, wird `fallback` verwendet. */
const getRelYear = (dateStr: string | null | undefined, fallback: number): number => {
  if (!dateStr) return fallback;
  const match = dateStr.match(/\d{4}/);
  return match ? parseInt(match[0], 10) : fallback;
};

// ─── Layout-Engine ───────────────────────────────────────────────────────────

export class FamilyTreeLayoutEngine {
  private nodeMap         = new Map<string, TreeNode>();
  private coupleMap       = new Map<string, FamilyUnit>();
  private personFamilies  = new Map<string, string[]>();
  private childToParentCouple = new Map<string, string>();
  private generations     = new Map<string, number>();

  private personPositions = new Map<string, { x: number; y: number }>();
  private symbolPositions: LayoutCoupleSymbol[] = [];
  private familyLines:     LayoutFamilyLine[]   = [];

  private coupleAnchor     = new Map<string, string>();      // coupleId → personId (deterministisch)
  private partners         = new Map<string, Set<string>>(); // Same-Rank-Partner
  private rankOrder        = new Map<number, string[]>();     // Ebene → IDs (links→rechts)

  private blockWidthCache  = new Map<string, number>();
  private familyWidthCache = new Map<string, number>();
  private layoutVisited    = new Set<string>();          // Rekursionsschutz für layoutPersonBlock
  private familyColorCounter = 0;

  // ─── Fokus-Linie ("Eigene Linie zentrieren") ────────────────────────────────
  private spine          = new Set<string>(); // Personen auf der direkten Vorfahren-Kette
  private spineCouples   = new Set<string>(); // Paare, die zwei Spine-Personen verbinden
  private spineDownCouple = new Map<string, string>(); // Spine-Person → ihr Paar, das das nächste Spine-Kind erzeugt
  private ancDepthCache  = new Map<string, number>();  // Person → Höhe ihres Ahnen-Teilbaums
  private spineActive    = false;

  /** @param focusId Person, deren direkte Vorfahren-Linie senkrecht ausgerichtet wird (null = aus) */
  constructor(private data: TreeData, private focusId: string | null = null) {}

  compute(): FamilyTreeLayoutResult {
    this.buildNodeMap();
    this.buildCoupleMap();
    this.buildSpine();
    this.buildPersonFamilies();
    this.computeGenerations();
    this.assignAnchors();          // deterministische Verankerung (reihenfolge-unabhängig)
    this.buildPartners();
    this.layoutAll();              // kompakte rekursive Platzierung (Subtree-Breiten)
    this.resolveOverlaps();
    this.recenterParents();
    this.resolveOverlaps();
    this.deriveOrderFromPositions();
    this.snapCoupleBlocks();       // starre Partner-Ketten auf exakt 240 einrasten
    this.alignSpineColumn();       // Fokus-Linie exakt senkrecht ausrichten (falls aktiv)
    this.rebuildVisuals();
    this.normalize();
    return this.collectResults();
  }

  // Same-rank-Partner (nur 2-Eltern-Paare) – Basis für starre Ketten-Blöcke
  private buildPartners() {
    this.partners = new Map();
    const add = (a: string, b: string) => {
      if (!this.partners.has(a)) this.partners.set(a, new Set());
      this.partners.get(a)!.add(b);
    };
    for (const u of this.coupleMap.values()) {
      if (u.p2Id) { add(u.p1Id, u.p2Id); add(u.p2Id, u.p1Id); }
    }
  }

  // Reihenfolge je Ebene aus den (approximativen) X-Werten der rekursiven Platzierung
  private deriveOrderFromPositions() {
    this.rankOrder = new Map();
    for (const [id, pos] of this.personPositions) {
      const r = this.generations.get(id) ?? Math.round(pos.y / V_STEP);
      if (!this.rankOrder.has(r)) this.rankOrder.set(r, []);
      this.rankOrder.get(r)!.push(id);
    }
    for (const ids of this.rankOrder.values())
      ids.sort((a, b) => this.personPositions.get(a)!.x - this.personPositions.get(b)!.x);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Phase 1: Datenstrukturen aufbauen
  // ═══════════════════════════════════════════════════════════════════════════

  private buildNodeMap() {
    for (const n of this.data.nodes) this.nodeMap.set(n.id, n);
  }

  private buildCoupleMap() {
    // 1. Paare aus Beziehungs-Links erstellen
    for (const link of this.data.links) {
      const id = this.makeCoupleId(link.person1Id, link.person2Id);
      if (this.coupleMap.has(id)) {
        this.coupleMap.get(id)!.link = link;
      } else {
        this.coupleMap.set(id, {
          id, p1Id: link.person1Id, p2Id: link.person2Id, link, childIds: [],
        });
      }
    }

    // 2. Kinder zuordnen
    for (const node of this.data.nodes) {
      if (!node.primaryParentId) continue;
      let p2 = node.secondaryParentId ?? null;

      // Wenn kein zweiter Elternteil angegeben → aus Beziehungs-Links ableiten,
      // damit Kinder unter dem Paar-Symbol (Ringe/Herz) erscheinen und die
      // Ahnenlinie vom Symbol – nicht von einem einzelnen Elternteil – ausgeht
      if (!p2) {
        p2 = this.inferCoParent(node.primaryParentId, node);
      }

      const id = this.makeCoupleId(node.primaryParentId, p2);
      if (!this.coupleMap.has(id)) {
        this.coupleMap.set(id, {
          id, p1Id: node.primaryParentId, p2Id: p2,
          link: null, childIds: [],
        });
      }
      this.coupleMap.get(id)!.childIds.push(node.id);
      this.childToParentCouple.set(node.id, id);
    }

    // 3. Kinder chronologisch sortieren
    for (const unit of this.coupleMap.values()) {
      unit.childIds.sort((a, b) =>
        getSortYear(this.nodeMap.get(a)?.birthDateText) -
        getSortYear(this.nodeMap.get(b)?.birthDateText)
      );
    }
  }

  /**
   * Baut die Fokus-Linie auf: vom Fokus-Knoten aufwärts über primaryParentId.
   * `spine` = alle Personen der Kette, `spineCouples` = die Paare, die jeweils
   * eine Spine-Person mit ihrem direkten Elternteil verbinden. Über diese Paare
   * wird das jeweilige Spine-Kind später mittig unter dem Paar-Symbol platziert,
   * sodass die Linie senkrecht verläuft.
   */
  private buildSpine() {
    this.spine = new Set();
    this.spineCouples = new Set();
    this.spineDownCouple = new Map();
    this.spineActive = false;
    if (!this.focusId || !this.nodeMap.has(this.focusId)) return;

    const guard = new Set<string>();
    let cur: string | undefined = this.focusId ?? undefined;
    while (cur && this.nodeMap.has(cur) && !guard.has(cur)) {
      guard.add(cur);
      this.spine.add(cur);

      // Beide Elternteile als Kandidaten; die Achse folgt der Seite mit der
      // TIEFSTEN Ahnenlinie (meiste Generationen nach oben). Gleichstand →
      // primärer Elternteil. Dadurch landet der oberste Ahn auf der Mittelachse.
      const node: TreeNode = this.nodeMap.get(cur)!;
      const candidates: string[] = [node.primaryParentId, node.secondaryParentId]
        .filter((p): p is string => !!p && this.nodeMap.has(p));
      if (candidates.length === 0) break;

      let best: string = candidates[0];
      let bestDepth = this.ancestorDepth(best);
      for (let i = 1; i < candidates.length; i++) {
        const d = this.ancestorDepth(candidates[i]);
        if (d > bestDepth) { best = candidates[i]; bestDepth = d; }
      }

      const coupleId = this.childToParentCouple.get(cur);
      if (coupleId) {
        this.spineCouples.add(coupleId);
        // Dieses Paar erzeugt `cur`; aus Sicht von `best` (Spine-Elternteil)
        // ist es das „nach unten" (Richtung Fokus) führende Spine-Paar.
        this.spineDownCouple.set(best, coupleId);
      }
      cur = best;
    }
    // Nur sinnvoll, wenn es überhaupt eine Kette (Person + ≥1 Vorfahre) gibt
    this.spineActive = this.spine.size > 1;
  }

  /** Höhe des Ahnen-Teilbaums über einer Person (0 = keine erfassten Eltern). */
  private ancestorDepth(id: string, guard: Set<string> = new Set()): number {
    const cached = this.ancDepthCache.get(id);
    if (cached !== undefined) return cached;
    if (guard.has(id)) return 0; // Zyklus-Schutz (nicht cachen)
    guard.add(id);
    const node = this.nodeMap.get(id);
    const parents = [node?.primaryParentId, node?.secondaryParentId]
      .filter((p): p is string => !!p && this.nodeMap.has(p));
    const depth = parents.length
      ? 1 + Math.max(...parents.map((p) => this.ancestorDepth(p, guard)))
      : 0;
    guard.delete(id);
    this.ancDepthCache.set(id, depth);
    return depth;
  }

  /**
   * Ermittelt den zweiten Elternteil (Co-Parent) eines Kindes, wenn dieser
   * nicht explizit gesetzt ist – damit die Ahnenlinie vom Paar-Symbol
   * (Heirat/Scheidung) und nicht von einem einzelnen Elternteil ausgeht.
   *
   *  - Genau EINE Beziehung des Elternteils      → dieser Partner.
   *  - MEHRERE Beziehungen (z.B. geschieden +     → chronologische Zuordnung
   *    wieder verheiratet)                          über das Geburtsjahr des
   *                                                  Kindes vs. Beziehungs-
   *                                                  Zeiträume (start/end).
   *
   * Es wird nur bei EINDEUTIGER Zuordnung ein Partner zurückgegeben; sonst
   * null → sicherer Rückfall auf Ein-Eltern-Darstellung statt Falschzuordnung.
   */
  private inferCoParent(personId: string, child: TreeNode): string | null {
    const rels: Array<{ partner: string; link: TreeLink }> = [];
    for (const unit of this.coupleMap.values()) {
      if (!unit.link || !unit.p2Id) continue; // Nur echte Beziehungseinträge
      if (unit.p1Id === personId) rels.push({ partner: unit.p2Id, link: unit.link });
      else if (unit.p2Id === personId) rels.push({ partner: unit.p1Id, link: unit.link });
    }

    if (rels.length === 0) return null;
    if (rels.length === 1) return rels[0].partner;

    // Mehrere Beziehungen → anhand des Geburtsjahres dem passenden Zeitraum zuordnen
    const birthYear = getSortYear(child.birthDateText);
    if (birthYear >= 9999) return null; // kein Geburtsjahr → nicht raten

    const matches = rels.filter(({ link }) => {
      const start = getRelYear(link.startDate, -Infinity);
      const end = getRelYear(link.endDate, Infinity);
      return birthYear >= start && birthYear <= end;
    });
    if (matches.length === 1) return matches[0].partner;

    // Mehrere Zeiträume passen – typischer Fall: Scheidung ohne erfasstes
    // Enddatum (Zeitraum läuft bis ∞) + neue Ehe. Beziehungen verlaufen in
    // der Regel sequenziell → die zuletzt VOR der Geburt begonnene Beziehung
    // ist die zur Geburt aktive. Nur bei eindeutigem, bekanntem Maximum
    // zuordnen; sonst null (sicherer Rückfall auf Ein-Eltern-Darstellung).
    if (matches.length > 1) {
      let best: { partner: string; start: number } | null = null;
      let tie = false;
      for (const { partner, link } of matches) {
        const start = getRelYear(link.startDate, -Infinity);
        if (!best || start > best.start) {
          best = { partner, start };
          tie = false;
        } else if (start === best.start) {
          tie = true;
        }
      }
      if (best && best.start !== -Infinity && !tie) return best.partner;
    }
    return null;
  }

  private buildPersonFamilies() {
    // Familien mit Kindern zuerst (wichtiger für Layout-Reihenfolge)
    for (const pass of [true, false]) {
      for (const [coupleId, unit] of this.coupleMap) {
        if (pass !== (unit.childIds.length > 0)) continue;
        for (const pid of [unit.p1Id, unit.p2Id]) {
          if (!pid) continue;
          if (!this.personFamilies.has(pid)) this.personFamilies.set(pid, []);
          const fams = this.personFamilies.get(pid)!;
          if (!fams.includes(coupleId)) fams.push(coupleId);
        }
      }
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Phase 2: Generationen berechnen (BFS von Wurzeln)
  // ═══════════════════════════════════════════════════════════════════════════

  private computeGenerations() {
    const childToParents  = new Map<string, string[]>();
    const parentToChildren = new Map<string, string[]>();

    for (const node of this.data.nodes) {
      if (!node.primaryParentId) continue;
      const parents = [node.primaryParentId];
      if (node.secondaryParentId) parents.push(node.secondaryParentId);
      childToParents.set(node.id, parents);
      for (const pid of parents) {
        if (!parentToChildren.has(pid)) parentToChildren.set(pid, []);
        parentToChildren.get(pid)!.push(node.id);
      }
    }

    // BFS abwärts von Wurzeln (initiale Zuweisung)
    const roots = this.data.nodes.filter(n => !n.primaryParentId);
    const queue: Array<{ id: string; gen: number }> = [];
    for (const r of roots) {
      this.generations.set(r.id, 0);
      queue.push({ id: r.id, gen: 0 });
    }

    // Die Tiefengrenze ist kein Schönheitsmaß, sondern der Riegel gegen einen
    // Zyklus in der Elternkette: Ist jemand (über Umwege) sein eigener Vorfahr,
    // liefe dieses BFS ewig und zählte die Generation dabei immer weiter hoch.
    // Mehr Ebenen als Personen kann es in einem gesunden Baum nicht geben.
    const MAX_TIEFE = this.data.nodes.length + 1;

    while (queue.length > 0) {
      const { id, gen } = queue.shift()!;
      if (gen >= MAX_TIEFE) continue;
      for (const cid of (parentToChildren.get(id) ?? [])) {
        const cGen = gen + 1;
        const cur = this.generations.get(cid);
        if (cur === undefined || cGen > cur) {
          this.generations.set(cid, cGen);
          queue.push({ id: cid, gen: cGen });
        }
      }
    }

    // Der Stand aus dem BFS, bevor die Feinarbeit beginnt.
    //
    // Er ist unsere Rückfallebene: Er stammt allein aus der Elternkette und
    // setzt jedes Kind garantiert unter seine Eltern. Hübsch ist er nicht –
    // Partner stehen womöglich auf verschiedenen Ebenen –, aber er ist immer
    // endlich und immer widerspruchsfrei.
    const ausBfs = new Map(this.generations);

    // Iterative Konvergenz: 3 Regeln bis stabil
    //   1. Partner auf gleiche Generation (max)
    //   2. Kinder mindestens 1 unter Eltern
    //   3. Eltern maximal 1 über Kindern (Lücken schließen!)
    //
    // Diese drei Regeln sind nicht immer zugleich erfüllbar. Heiratet jemand
    // in die eigene Verwandtschaft – Cousinen, Onkel und Nichte, oder schlicht
    // ein Fehler in der Datei –, entsteht ein Ringschluss: Regel 1 hebt einen
    // Partner an, Regel 2 schiebt daraufhin dessen Kinder nach unten, Regel 3
    // zieht deren Eltern wieder hoch, und Regel 1 fängt von vorn an. Jede Runde
    // ändert etwas, also endet die Schleife nie.
    //
    // Gemessen an einer GEDCOM-Datei mit 3000 Personen: nach 21 000 Durchläufen
    // standen 10 507 Generationen zu Buche, Tendenz weiter steigend. Für den
    // Nutzer sah das so aus, dass die Anwendung nach dem Import einfror – der
    // Browser-Tab hing an dieser Stelle, nicht an der Datenmenge.
    //
    // Ein gesunder Baum ist nach einer Handvoll Runden stabil; die Grenze ist
    // großzügig gewählt und wird im Normalfall nie erreicht.
    const MAX_DURCHLAEUFE = 200;

    let changed = true;
    let durchlauf = 0;
    while (changed) {
      if (++durchlauf > MAX_DURCHLAEUFE) {
        // Die Daten widersprechen sich. Statt zu raten, welche Regel gilt,
        // gehen wir auf den Stand zurück, der garantiert widerspruchsfrei ist,
        // und wenden die beiden wichtigen Regeln noch je einmal an: Partner
        // zusammen (Regel 1) und Kinder unter die Eltern (Regel 2). Regel 3
        // bleibt weg – sie ist die kosmetische, und sie ist es, die den
        // Ringschluss antreibt.
        this.generations = new Map(ausBfs);
        for (const link of this.data.links) this.syncGen(link.person1Id, link.person2Id);
        for (const unit of this.coupleMap.values()) {
          if (unit.p2Id) this.syncGen(unit.p1Id, unit.p2Id);
        }
        for (const [cid, pids] of childToParents) {
          const pGen = Math.max(...pids.map((p) => this.generations.get(p) ?? 0));
          if ((this.generations.get(cid) ?? 0) <= pGen) this.generations.set(cid, pGen + 1);
        }
        break;
      }
      changed = false;

      // Regel 1: Partner synchronisieren
      for (const link of this.data.links) {
        if (this.syncGen(link.person1Id, link.person2Id)) changed = true;
      }
      for (const unit of this.coupleMap.values()) {
        if (unit.p2Id && this.syncGen(unit.p1Id, unit.p2Id)) changed = true;
      }

      // Regel 2: Kinder müssen UNTER ihren Eltern liegen
      for (const [cid, pids] of childToParents) {
        const pGen = Math.max(...pids.map(p => this.generations.get(p) ?? 0));
        const cGen = this.generations.get(cid) ?? 0;
        if (cGen <= pGen) {
          this.generations.set(cid, pGen + 1);
          changed = true;
        }
      }

      // Regel 3: Eltern maximal 1 Generation über Kindern
      // (verhindert lange Linien über mehrere leere Ebenen)
      for (const [cid, pids] of childToParents) {
        const cGen = this.generations.get(cid) ?? 0;
        for (const pid of pids) {
          const pGen = this.generations.get(pid) ?? 0;
          if (pGen < cGen - 1) {
            this.generations.set(pid, cGen - 1);
            changed = true;
          }
        }
      }
    }

    // Generationen normalisieren: Minimum auf 0 setzen
    let minGen = Infinity;
    for (const gen of this.generations.values()) {
      if (gen < minGen) minGen = gen;
    }
    if (minGen > 0 && minGen !== Infinity) {
      for (const [id, gen] of this.generations) {
        this.generations.set(id, gen - minGen);
      }
    }

    // Fallback für nicht erreichbare Personen
    for (const n of this.data.nodes) {
      if (!this.generations.has(n.id)) this.generations.set(n.id, 0);
    }
  }

  /** Synchronisiert zwei Partner auf die gleiche Generation. Gibt true zurück bei Änderung. */
  private syncGen(id1: string, id2: string): boolean {
    const g1 = this.generations.get(id1);
    const g2 = this.generations.get(id2);
    if (g1 !== undefined && g2 === undefined) {
      this.generations.set(id2, g1);
      return true;
    } else if (g2 !== undefined && g1 === undefined) {
      this.generations.set(id1, g2);
      return true;
    } else if (g1 !== undefined && g2 !== undefined && g1 !== g2) {
      const m = Math.max(g1, g2);
      this.generations.set(id1, m);
      this.generations.set(id2, m);
      return true;
    }
    return false;
  }

  private getGenY(personId: string): number {
    return (this.generations.get(personId) ?? 0) * V_STEP;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Phase 2b: Deterministische Anker-Zuweisung (reihenfolge-unabhängig)
  // ═══════════════════════════════════════════════════════════════════════════

  /** Anker einer Familieneinheit = der Partner mit den meisten Partnerschaften
   *  (der "Hub"). Tiebreak: der Partner, der selbst Kind im Baum ist (hält das
   *  Paar unter der Blutlinie); danach stabil per ID. Dadurch ist das Layout
   *  unabhängig von der Knoten-/DB-Reihenfolge – im Gegensatz zur alten
   *  familyOwner-Logik ("wer zuerst kommt"). */
  private assignAnchors() {
    const famCount = (id: string) => this.personFamilies.get(id)?.length ?? 0;
    for (const unit of this.coupleMap.values()) {
      if (!unit.p2Id) { this.coupleAnchor.set(unit.id, unit.p1Id); continue; }
      const a = unit.p1Id, b = unit.p2Id;
      const ca = famCount(a), cb = famCount(b);
      let anchor: string;
      if (ca !== cb) {
        anchor = ca > cb ? a : b;
      } else {
        const aChild = !!this.nodeMap.get(a)?.primaryParentId;
        const bChild = !!this.nodeMap.get(b)?.primaryParentId;
        if (aChild !== bChild) anchor = aChild ? a : b;
        else anchor = a < b ? a : b;
      }
      this.coupleAnchor.set(unit.id, anchor);
    }
  }

  private ownsFamily(personId: string, coupleId: string): boolean {
    return this.coupleAnchor.get(coupleId) === personId;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Phase 3: Breitenberechnung (bottom-up, mit Cache)
  // ═══════════════════════════════════════════════════════════════════════════

  /** Breite des "Blocks" einer Person inkl. aller Nachkommen-Familien.
   *  Es zählen nur Familien, deren deterministischer Anker diese Person ist. */
  private personBlockWidth(personId: string): number {
    if (this.blockWidthCache.has(personId)) return this.blockWidthCache.get(personId)!;
    this.blockWidthCache.set(personId, NODE_W); // Sentinel gegen Zyklen

    const families = this.personFamilies.get(personId);
    if (!families || families.length === 0) return NODE_W;

    let total = 0;
    for (const familyId of families) {
      if (!this.ownsFamily(personId, familyId)) continue; // nur eigene (verankerte) Familien
      if (total > 0) total += FAMILY_GAP;
      total += this.familySubtreeWidth(familyId);
    }

    const result = Math.max(NODE_W, total || NODE_W);
    this.blockWidthCache.set(personId, result);
    return result;
  }

  /** Breite einer Familieneinheit inkl. aller Kinder-Teilbäume */
  private familySubtreeWidth(coupleId: string): number {
    if (this.familyWidthCache.has(coupleId)) return this.familyWidthCache.get(coupleId)!;

    const unit = this.coupleMap.get(coupleId);
    if (!unit) return NODE_W;

    const coupleW = unit.p2Id
      ? NODE_W + PARTNER_GAP + SYMBOL_W + PARTNER_GAP + NODE_W
      : NODE_W;

    if (unit.childIds.length === 0) {
      this.familyWidthCache.set(coupleId, coupleW);
      return coupleW;
    }

    let childrenW = 0;
    for (let i = 0; i < unit.childIds.length; i++) {
      if (i > 0) childrenW += SIBLING_GAP;
      childrenW += this.personBlockWidth(unit.childIds[i]);
    }

    const result = Math.max(coupleW, childrenW);
    this.familyWidthCache.set(coupleId, result);
    return result;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Phase 4: Platzierung (top-down, rekursiv)
  // ═══════════════════════════════════════════════════════════════════════════

  private layoutAll() {
    // 1. Verbundene Familien-Komponenten berechnen (Union-Find)
    const components = this.buildConnectedComponents();

    // 2. Größte Komponente zuerst (Hauptfamilie prominent links)
    components.sort((a, b) => b.length - a.length);

    let currentX = 0;

    for (const component of components) {
      // 3. Wurzeln dieser Komponente: Personen ohne Eltern
      const compRoots = component.filter(id => {
        const node = this.nodeMap.get(id);
        return node && !node.primaryParentId;
      });

      // Sortierung: niedrigste Generation zuerst; bei Gleichstand Hubs (mehr
      // Partnerschaften) zuerst – konsistent mit der Anker-Logik –, dann
      // meiste Nachkommen.
      compRoots.sort((a, b) => {
        const gA = this.generations.get(a) ?? 0;
        const gB = this.generations.get(b) ?? 0;
        if (gA !== gB) return gA - gB;
        const fA = this.personFamilies.get(a)?.length ?? 0;
        const fB = this.personFamilies.get(b)?.length ?? 0;
        if (fA !== fB) return fB - fA;
        return this.countDescendants(b) - this.countDescendants(a);
      });

      // 4. Wurzeln dieser Komponente platzieren
      for (const rootId of compRoots) {
        if (this.personPositions.has(rootId)) continue;

        const w = this.personBlockWidth(rootId);

        // Bereits platzierte Nachkommen? → In deren Nähe platzieren
        const nearX = this.findPlacedDescendantX(rootId);
        if (nearX !== null) {
          this.layoutPersonBlock(rootId, nearX - w / 2, w);
        } else {
          this.layoutPersonBlock(rootId, currentX, w);
        }

        // currentX hinter dem rechtesten Element fortsetzen
        const allXs = Array.from(this.personPositions.values()).map(p => p.x);
        if (allXs.length > 0) {
          currentX = Math.max(...allXs) + NODE_W / 2 + FAMILY_GAP;
        }
      }

      // Nicht-Wurzel-Personen, die noch nicht platziert wurden (Sicherheitsnetz)
      for (const id of component) {
        if (this.personPositions.has(id)) continue;
        this.personPositions.set(id, {
          x: currentX + NODE_W / 2,
          y: this.getGenY(id),
        });
        currentX += NODE_W + SIBLING_GAP;
      }

      // Abstand zwischen Komponenten
      if (this.personPositions.size > 0) {
        const allXs = Array.from(this.personPositions.values()).map(p => p.x);
        currentX = Math.max(...allXs) + NODE_W / 2 + FAMILY_GAP;
      }
    }
  }

  /** Union-Find: Verbundene Familien-Komponenten berechnen */
  private buildConnectedComponents(): string[][] {
    const parent = new Map<string, string>();

    const find = (x: string): string => {
      if (!parent.has(x)) parent.set(x, x);
      if (parent.get(x) !== x) parent.set(x, find(parent.get(x)!));
      return parent.get(x)!;
    };

    const union = (a: string, b: string) => {
      const ra = find(a), rb = find(b);
      if (ra !== rb) parent.set(ra, rb);
    };

    // Alle Personen initialisieren
    for (const node of this.data.nodes) find(node.id);

    // Durch Beziehungs-Links verbinden
    for (const link of this.data.links) {
      union(link.person1Id, link.person2Id);
    }

    // Durch Eltern-Kind verbinden
    for (const node of this.data.nodes) {
      if (node.primaryParentId) union(node.id, node.primaryParentId);
      if (node.secondaryParentId) union(node.id, node.secondaryParentId);
    }

    // Durch coupleMap verbinden (inferPartner-Zuordnungen)
    for (const unit of this.coupleMap.values()) {
      if (unit.p2Id) union(unit.p1Id, unit.p2Id);
      for (const cid of unit.childIds) union(unit.p1Id, cid);
    }

    // Gruppen bilden
    const groups = new Map<string, string[]>();
    for (const node of this.data.nodes) {
      const root = find(node.id);
      if (!groups.has(root)) groups.set(root, []);
      groups.get(root)!.push(node.id);
    }

    return Array.from(groups.values());
  }

  /** Zählt alle Nachkommen einer Person (BFS über Familien) */
  private countDescendants(personId: string): number {
    let count = 0;
    const visited = new Set<string>([personId]);
    const queue = [personId];

    while (queue.length > 0) {
      const pid = queue.shift()!;
      const families = this.personFamilies.get(pid);
      if (!families) continue;

      for (const fid of families) {
        const unit = this.coupleMap.get(fid);
        if (!unit) continue;
        for (const cid of unit.childIds) {
          if (visited.has(cid)) continue;
          visited.add(cid);
          count++;
          queue.push(cid);
        }
      }
    }
    return count;
  }

  /** BFS: Sucht X-Wert eines bereits platzierten Nachkommen */
  private findPlacedDescendantX(personId: string): number | null {
    const visited = new Set<string>([personId]);
    const queue = [personId];

    while (queue.length > 0) {
      const pid = queue.shift()!;
      const families = this.personFamilies.get(pid);
      if (!families) continue;

      for (const coupleId of families) {
        const unit = this.coupleMap.get(coupleId);
        if (!unit) continue;
        for (const childId of unit.childIds) {
          if (visited.has(childId)) continue;
          visited.add(childId);
          if (this.personPositions.has(childId)) {
            return this.personPositions.get(childId)!.x;
          }
          queue.push(childId);
        }
      }
    }
    return null;
  }

  /** Platziert eine Person und alle ihre Nachkommen-Familien.
   * Nur Familien, deren deterministischer Anker diese Person ist, werden platziert.
   * Wenn die Person bereits platziert ist (z.B. durch Einheirat), werden
   * ihre eigenen Familien trotzdem gelayoutet.
   *
   * Geschieden/getrennte Beziehungen (Ex-Partner) werden auf die LINKE Seite
   * der Person platziert, aktuelle Beziehungen (verheiratet/Partnerschaft) auf
   * die RECHTE Seite. Dadurch überdecken sich ehemalige und aktuelle Partner
   * nicht mehr. */
  private layoutPersonBlock(personId: string, left: number, allocatedWidth: number) {
    if (this.layoutVisited.has(personId)) return; // Rekursionsschutz
    this.layoutVisited.add(personId);

    const alreadyPlaced = this.personPositions.has(personId);
    const families = this.personFamilies.get(personId);
    const y = this.getGenY(personId);

    // Eigene Familien filtern (nur die verankerten, die bei der Breitenberechnung gezählt wurden)
    const ownedFamilies = families?.filter(fid => this.ownsFamily(personId, fid));

    // Blatt-Person ohne eigene Familien
    if (!ownedFamilies || ownedFamilies.length === 0) {
      if (!alreadyPlaced) {
        this.personPositions.set(personId, { x: left + allocatedWidth / 2, y });
      }
      return;
    }

    // ─── NEUE LOGIK FÜR MEHRERE PARTNER (Split-Modus) ───────────────────
    const leftFams: string[] = [];
    const rightFams: string[] = [];
    const remainingFams: string[] = [];

    // 1. Zuerst bekannte Ex-Partner (geschieden/getrennt) nach links sortieren
    for (const fid of ownedFamilies) {
      const unit = this.coupleMap.get(fid);
      const t = unit?.link?.type;
      if (t === 'divorced' || t === 'separated') {
        leftFams.push(fid);
      } else {
        remainingFams.push(fid);
      }
    }

    // 2. Den Rest abwechselnd verteilen, damit das Layout bei mehreren
    //    Partnern (auch ohne Ex-Status) immer ausbalanciert ist.
    for (const fid of remainingFams) {
      if (rightFams.length <= leftFams.length) {
        rightFams.push(fid); // Erster Partner nach rechts
      } else {
        leftFams.push(fid);  // Zweiter Partner nach links
      }
    }

    const splitMode = leftFams.length > 0 && rightFams.length > 0;
    const halfCouple = SYMBOL_W / 2 + PARTNER_GAP + NODE_W / 2;

    // ─── Fall A: Nur eine Sorte (oder keine Split-Situation) ──
    if (!splitMode) {
      const totalFamW = ownedFamilies.reduce((sum, fid, i) => {
        return sum + this.familySubtreeWidth(fid) + (i > 0 ? FAMILY_GAP : 0);
      }, 0);
      let famLeft = alreadyPlaced
        ? this.personPositions.get(personId)!.x - totalFamW / 2
        : left;
      for (const familyId of ownedFamilies) {
        const famW = this.familySubtreeWidth(familyId);
        this.layoutFamily(familyId, famLeft, famW);
        famLeft += famW + FAMILY_GAP;
      }
      return;
    }

    // ─── Fall B: Split-Modus (Links + Rechts verteilt) ────────────────
    const leftTotalW = leftFams.reduce((sum, fid, i) => {
      return sum + this.familySubtreeWidth(fid) + (i > 0 ? FAMILY_GAP : 0);
    }, 0);

    // 1) Rechte Familien platzieren, wobei die zentrale Person auf der
    //    LINKEN Seite ihres aktuellen Paares landen muss.
    let rightStart: number;
    if (!alreadyPlaced) {
      // Links Platz für linke Familien reservieren
      rightStart = left + leftTotalW + FAMILY_GAP;
    } else {
      // Person bereits platziert → rightStart so wählen, dass personId am
      // linken Rand des ersten Paares landet (durch Hint-Mechanismus).
      const firstRightFamW = this.familySubtreeWidth(rightFams[0]);
      const personX = this.personPositions.get(personId)!.x;
      rightStart = personX + halfCouple - firstRightFamW / 2;
    }

    let famLeft = rightStart;
    for (let i = 0; i < rightFams.length; i++) {
      const familyId = rightFams[i];
      const famW = this.familySubtreeWidth(familyId);
      // Hint nur bei der ersten rechten Familie: personId soll links sein
      const anchorLeftId = i === 0 ? personId : undefined;
      this.layoutFamily(familyId, famLeft, famW, anchorLeftId);
      famLeft += famW + FAMILY_GAP;
    }

    // 2) Linke Familien platzieren – in umgekehrter Reihenfolge, damit die
    //    erste linke Beziehung direkt neben personId steht.
    const personX = this.personPositions.get(personId)!.x;
    let rightEdgeForEx = personX - halfCouple; // Linker Rand der Paar-Zone von personId

    for (let i = leftFams.length - 1; i >= 0; i--) {
      const familyId = leftFams[i];
      const unit = this.coupleMap.get(familyId);
      if (!unit || !unit.p2Id) continue;

      const famW = this.familySubtreeWidth(familyId);
      const partnerId = unit.p1Id === personId ? unit.p2Id : unit.p1Id;

      // Linken Partner direkt links neben personId platzieren
      const partnerX = rightEdgeForEx - halfCouple;
      if (!this.personPositions.has(partnerId)) {
        this.personPositions.set(partnerId, { x: partnerX, y });
      }

      // layoutFamily aufrufen – beide Partner sind nun platziert, es werden
      // nur noch Kinder unter dem Paar-Symbol platziert.
      this.layoutFamily(familyId, partnerX - famW / 2, famW);

      rightEdgeForEx = partnerX - halfCouple - FAMILY_GAP;
    }
  }

  /** Platziert eine komplette Familieneinheit: Eltern + Kinder + Nachkommen.
   *  Optionaler `anchorLeftId`: gibt an, welcher Partner auf der LINKEN Seite
   *  des Paares landen soll. Wird genutzt, damit die zentrale Person einer
   *  Split-Anordnung (mit Ex-Partnern links) immer auf der inneren Seite steht. */
  private layoutFamily(
    coupleId: string,
    left: number,
    allocatedWidth: number,
    anchorLeftId?: string
  ) {
    const unit = this.coupleMap.get(coupleId);
    if (!unit) return;

    const center = left + allocatedWidth / 2;
    const y = this.getGenY(unit.p1Id);
    let symbolX = center;

    // ─── Elternpaar platzieren ───────────────────────────────────────────
    if (unit.p2Id) {
      const p1Here = this.personPositions.has(unit.p1Id);
      const p2Here = this.personPositions.has(unit.p2Id);
      const halfCouple = SYMBOL_W / 2 + PARTNER_GAP + NODE_W / 2;
      // Flip: Wenn die Ankerperson p2 ist, muss sie nach links → Seiten tauschen.
      const flip = anchorLeftId != null && anchorLeftId === unit.p2Id;

      if (!p1Here && !p2Here) {
        // Beide neu → zentriert platzieren
        const leftPerson  = flip ? unit.p2Id : unit.p1Id;
        const rightPerson = flip ? unit.p1Id : unit.p2Id;
        this.personPositions.set(leftPerson,  { x: center - halfCouple, y });
        this.personPositions.set(rightPerson, { x: center + halfCouple, y });
        symbolX = center;

      } else if (p1Here && !p2Here) {
        // p1 schon da → p2 daneben (standardmäßig rechts, ggf. links bei flip)
        const p1x = this.personPositions.get(unit.p1Id)!.x;
        const offset = NODE_W / 2 + PARTNER_GAP + SYMBOL_W + PARTNER_GAP + NODE_W / 2;
        const sign = flip ? -1 : 1;
        this.personPositions.set(unit.p2Id, { x: p1x + sign * offset, y });
        symbolX = p1x + (sign * offset) / 2;

      } else if (!p1Here && p2Here) {
        // p2 schon da → p1 daneben (standardmäßig links, ggf. rechts bei flip)
        const p2x = this.personPositions.get(unit.p2Id)!.x;
        const offset = NODE_W / 2 + PARTNER_GAP + SYMBOL_W + PARTNER_GAP + NODE_W / 2;
        const sign = flip ? 1 : -1;
        this.personPositions.set(unit.p1Id, { x: p2x + sign * offset, y });
        symbolX = p2x + (sign * offset) / 2;

      } else {
        // Beide schon platziert → Symbol dazwischen
        const p1x = this.personPositions.get(unit.p1Id)!.x;
        const p2x = this.personPositions.get(unit.p2Id)!.x;
        symbolX = (p1x + p2x) / 2;
      }
    } else {
      // Alleinerziehend
      if (!this.personPositions.has(unit.p1Id)) {
        this.personPositions.set(unit.p1Id, { x: center, y });
      }
      symbolX = this.personPositions.get(unit.p1Id)!.x;
    }

    // ─── Kinder platzieren ───────────────────────────────────────────────
    if (unit.childIds.length === 0) return;

    // Reihenfolge der Kinder. Im Fokus-Modus wird das Kind auf der direkten
    // Linie (Spine) mittig unter das Paar-Symbol gesetzt; die Geschwister
    // werden breiten-balanciert nach links/rechts verteilt (Reihenfolge je
    // Seite bleibt chronologisch). Dadurch verläuft die Linie senkrecht.
    let order = unit.childIds;
    const spineChild = this.spineActive && this.spineCouples.has(coupleId)
      ? unit.childIds.find(c => this.spine.has(c))
      : undefined;

    if (spineChild && unit.childIds.length > 1) {
      const left: string[] = [];
      const right: string[] = [];
      let lw = 0, rw = 0;
      for (const c of unit.childIds) {
        if (c === spineChild) continue;
        const w = this.personBlockWidth(c);
        if (lw <= rw) { left.push(c); lw += w; } else { right.push(c); rw += w; }
      }
      order = [...left, spineChild, ...right];
    }

    // Alle Kinder berücksichtigen (auch bereits platzierte, damit
    // Geschwister zusammen bleiben und nicht in fremden Familien landen)
    const childWidths = order.map(cid => this.personBlockWidth(cid));
    const totalChildW = childWidths.reduce((s, w) => s + w, 0)
      + (childWidths.length - 1) * SIBLING_GAP;

    // Standard: Kindergruppe unter dem Symbol zentrieren. Im Fokus-Modus den
    // Block so verschieben, dass das Spine-Kind so sitzt, dass SEIN späteres
    // Paar-Symbol genau unter DIESEM Symbol landet – dadurch verläuft die
    // Symbol-Kette exakt senkrecht (statt pro Generation um ein halbes Paar
    // nach rechts zu wandern).
    let childLeft: number;
    if (spineChild) {
      // Versatz: hat das Spine-Kind selbst ein Paar mit zweitem Partner, sitzt
      // dessen Symbol eine halbe Paarbreite neben dem Kind (Seite = ob das Kind
      // p1 oder p2 ist). Wir verschieben das Kind entsprechend gegen.
      let targetX = symbolX;
      const downId = this.spineDownCouple.get(spineChild);
      const down = downId ? this.coupleMap.get(downId) : undefined;
      if (down?.p2Id) {
        targetX += down.p1Id === spineChild ? -HALF_COUPLE : HALF_COUPLE;
      }
      const sIdx = order.indexOf(spineChild);
      let before = 0;
      for (let i = 0; i < sIdx; i++) before += childWidths[i] + SIBLING_GAP;
      childLeft = targetX - before - childWidths[sIdx] / 2;
    } else {
      childLeft = symbolX - totalChildW / 2;
    }

    for (let i = 0; i < order.length; i++) {
      const cid = order[i];
      const cw = childWidths[i];

      if (this.personPositions.has(cid) && !this.layoutVisited.has(cid)) {
        // Kind wurde durch Einheirat in einer anderen Familie platziert →
        // an die korrekte Geschwister-Position verschieben (inkl. Partner + Nachkommen)
        const oldX = this.personPositions.get(cid)!.x;
        const newX = childLeft + cw / 2;
        const dx = newX - oldX;
        if (Math.abs(dx) > 5) {
          this.personPositions.get(cid)!.x = newX;
          // Partner mitverschieben
          const childFamilies = this.personFamilies.get(cid);
          const visited = new Set<string>([cid]);
          if (childFamilies) {
            for (const fid of childFamilies) {
              const u = this.coupleMap.get(fid);
              if (!u?.p2Id) continue;
              const partnerId = u.p1Id === cid ? u.p2Id : u.p1Id;
              if (partnerId && !visited.has(partnerId)) {
                visited.add(partnerId);
                const pp = this.personPositions.get(partnerId);
                if (pp) pp.x += dx;
              }
            }
          }
          // Nachkommen verschieben
          this.shiftSubtree(cid, dx, visited);
        }
      }

      // Person und ihre eigenen Familien layouten (auch wenn schon platziert)
      this.layoutPersonBlock(cid, childLeft, cw);
      childLeft += cw + SIBLING_GAP;
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Hilfsfunktion: Teilbaum (Nachkommen + deren Partner) horizontal verschieben
  // ═══════════════════════════════════════════════════════════════════════════

  private shiftSubtree(personId: string, dx: number, visited: Set<string>) {
    const families = this.personFamilies.get(personId);
    if (!families) return;

    for (const coupleId of families) {
      const unit = this.coupleMap.get(coupleId);
      if (!unit) continue;

      for (const childId of unit.childIds) {
        if (visited.has(childId)) continue;
        visited.add(childId);

        const cp = this.personPositions.get(childId);
        if (cp) cp.x += dx;

        // Partner des Kindes mitverschieben
        const cf = this.personFamilies.get(childId);
        if (cf) {
          for (const ccid of cf) {
            const cu = this.coupleMap.get(ccid);
            if (!cu?.p2Id) continue;
            const pid = cu.p1Id === childId ? cu.p2Id : cu.p1Id;
            if (!visited.has(pid)) {
              visited.add(pid);
              const pp = this.personPositions.get(pid);
              if (pp) pp.x += dx;
            }
          }
        }

        this.shiftSubtree(childId, dx, visited);
      }
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Phase 5a: Aufräum-Pässe auf der rekursiven Platzierung
  // ═══════════════════════════════════════════════════════════════════════════

  /** Löst Überlappungen innerhalb einer Ebene ordnungserhaltend auf: gleiche
   *  Höhe gruppieren (Paare zusammenhalten), nach links sortieren, nach rechts
   *  schieben (Subtree wandert mit). */
  private resolveOverlaps() {
    const byLevel = new Map<number, string[]>();
    for (const [id, pos] of this.personPositions) {
      const y = Math.round(pos.y);
      if (!byLevel.has(y)) byLevel.set(y, []);
      byLevel.get(y)!.push(id);
    }
    for (const y of Array.from(byLevel.keys()).sort((a, b) => a - b)) {
      const ids = byLevel.get(y)!; if (ids.length < 2) continue;
      ids.sort((a, b) => this.personPositions.get(a)!.x - this.personPositions.get(b)!.x);
      const visited = new Set<string>();
      const groups: { ids: string[]; left: number; right: number }[] = [];
      for (const id of ids) {
        if (visited.has(id)) continue; visited.add(id);
        const pos = this.personPositions.get(id)!;
        const group = { ids: [id], left: pos.x - NODE_W / 2, right: pos.x + NODE_W / 2 };
        const fams = this.personFamilies.get(id);
        if (fams) for (const fid of fams) {
          const u = this.coupleMap.get(fid); if (!u?.p2Id) continue;
          const pid = u.p1Id === id ? u.p2Id : u.p1Id;
          const pp = this.personPositions.get(pid);
          if (pp && Math.abs(pp.y - y) < 10 && !visited.has(pid)) {
            visited.add(pid); group.ids.push(pid);
            group.left = Math.min(group.left, pp.x - NODE_W / 2);
            group.right = Math.max(group.right, pp.x + NODE_W / 2);
          }
        }
        groups.push(group);
      }
      groups.sort((a, b) => a.left - b.left);
      for (let i = 1; i < groups.length; i++) {
        const overlap = groups[i - 1].right + SIBLING_GAP - groups[i].left;
        if (overlap <= 0) continue;
        const shiftVisited = new Set<string>();
        for (const g of groups) for (const nid of g.ids) shiftVisited.add(nid);
        for (let j = i; j < groups.length; j++) {
          for (const nid of groups[j].ids) {
            this.shiftSubtree(nid, overlap, shiftVisited);
            const p = this.personPositions.get(nid); if (p) p.x += overlap;
          }
          groups[j].left += overlap; groups[j].right += overlap;
        }
      }
    }
  }

  /** Zentriert einfache Eltern-Paare (keine Hubs) exakt über ihre Kinder –
   *  behebt diagonale Verbindungslinien. Von unten nach oben. */
  private recenterParents() {
    const list: [number, FamilyUnit][] = [];
    for (const u of this.coupleMap.values()) {
      if (!u.childIds.length) continue;
      list.push([this.generations.get(u.p1Id) ?? 0, u]);
    }
    list.sort((a, b) => b[0] - a[0]);
    for (const [, unit] of list) {
      // Spine-Paare bleiben über ihrem Spine-Kind stehen (von der Rekursion
      // gesetzt) – nicht auf die Geschwister-Mitte zurückzentrieren, sonst
      // verbiegt sich die senkrechte Fokus-Linie wieder.
      if (this.spineCouples.has(unit.id)) continue;
      const p1Fams = this.personFamilies.get(unit.p1Id)?.length || 0;
      const p2Fams = unit.p2Id ? (this.personFamilies.get(unit.p2Id)?.length || 0) : 0;
      if (p1Fams > 1 || p2Fams > 1) continue; // Hubs nicht nachträglich verschieben
      const placed = unit.childIds.filter(c => this.personPositions.has(c));
      if (!placed.length) continue;
      const xs = placed.map(c => this.personPositions.get(c)!.x);
      const childCenter = (Math.min(...xs) + Math.max(...xs)) / 2;
      const p1 = this.personPositions.get(unit.p1Id); if (!p1) continue;
      let parentCenter: number;
      if (unit.p2Id) {
        const p2 = this.personPositions.get(unit.p2Id); if (!p2) continue;
        parentCenter = (p1.x + p2.x) / 2;
      } else parentCenter = p1.x;
      const dx = childCenter - parentCenter;
      if (Math.abs(dx) < 5) continue;
      p1.x += dx;
      if (unit.p2Id) { const p2 = this.personPositions.get(unit.p2Id); if (p2) p2.x += dx; }
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Phase 5b: Couple-Blöcke einrasten (einmalig, ohne Iteration)
  // ═══════════════════════════════════════════════════════════════════════════
  //
  // Nimmt die kompakte rekursive Platzierung (Phase 5/5a) als Vorgabe und rastet
  // jede Partner-Kette (E1–M–F–E2) als STARREN Block mit exakt 240er-Abständen
  // ein:  block.x = Mittel über (rekursiveX(m) − offset(m))  → minimale
  // Verschiebung. Anschließend werden Überlappungen je Ebene ordnungserhaltend
  // nach rechts aufgelöst.
  //
  // Warum kein iteratives Relaxieren mehr: Die Barycenter-Relaxation konvergiert
  // bei tiefen/breiten Ahnentafeln (Pedigree) nicht und sprengte die Breite
  // (>4×). Das rekursive Layout ist bereits kompakt und ausgerichtet – hier wird
  // es nur auf exakte Paarabstände eingerastet. Pedigree-Paare liegen schon
  // ~240 auseinander → Snap verändert dort praktisch nichts (Breite bleibt 1.0×).

  private snapCoupleBlocks() {
    const COUPLE_DIST = 2 * HALF_COUPLE; // 240
    const recX = new Map<string, number>();
    for (const [id, p] of this.personPositions) recX.set(id, p.x);

    const blocks: LayoutBlock[] = [];
    const blockOf = new Map<string, LayoutBlock>();

    for (const [rank, ids] of this.rankOrder) {
      const orderIdx = new Map(ids.map((id, i) => [id, i] as const));
      const seen = new Set<string>();
      for (const startId of ids) {
        if (seen.has(startId)) continue;
        const comp: string[] = []; const q = [startId]; seen.add(startId);
        while (q.length) {
          const n = q.shift()!; comp.push(n);
          for (const p of (this.partners.get(n) ?? [])) if (orderIdx.has(p) && !seen.has(p)) { seen.add(p); q.push(p); }
        }
        const ordered = this.orderPartnerChain(comp, orderIdx);
        const offset = new Map<string, number>();
        for (let i = 0; i < ordered.length; i++) offset.set(ordered[i], i * COUPLE_DIST);
        const vals = [...offset.values()]; const mid = (Math.min(...vals) + Math.max(...vals)) / 2;
        for (const k of offset.keys()) offset.set(k, offset.get(k)! - mid);
        // block.x so wählen, dass die Mitglieder im Mittel an ihrer rekursiven
        // X-Position bleiben (minimale Verschiebung gegenüber dem Kompakt-Layout)
        const bx = ordered.reduce((s, m) => s + (recX.get(m)! - offset.get(m)!), 0) / ordered.length;
        const blockOrder = Math.min(...ordered.map(id => orderIdx.get(id)!));
        const block: LayoutBlock = {
          rank, members: ordered, offset, x: bx,
          minO: Math.min(...vals) - mid, maxO: Math.max(...vals) - mid, order: blockOrder,
        };
        blocks.push(block); for (const m of ordered) blockOf.set(m, block);
      }
    }

    // Überlappungen je Ebene auflösen: nach linker Kante sortieren, push-right
    const blocksByRank = new Map<number, LayoutBlock[]>();
    for (const b of blocks) { if (!blocksByRank.has(b.rank)) blocksByRank.set(b.rank, []); blocksByRank.get(b.rank)!.push(b); }
    for (const bs of blocksByRank.values()) {
      bs.sort((a, b) => (a.x + a.minO) - (b.x + b.minO));
      for (let i = 1; i < bs.length; i++) {
        const prev = bs[i - 1], cur = bs[i];
        const need = (prev.x + prev.maxO + NODE_W / 2 + SIBLING_GAP) - (cur.x + cur.minO - NODE_W / 2);
        if (need > 0) cur.x += need;
      }
    }

    for (const b of blocks) for (const m of b.members) this.personPositions.get(m)!.x = b.x + b.offset.get(m)!;
  }

  /** Ordnet eine Partner-Komponente als Pfad (E1–M–F–E2), damit aufeinander-
   *  folgende Mitglieder echte Partner sind. Orientierung links→rechts nach dem
   *  X-Hinweis der rekursiven Platzierung. Sterne (Grad > 2) als best effort. */
  private orderPartnerChain(comp: string[], orderIdx: Map<string, number>): string[] {
    if (comp.length <= 1) return comp.slice();
    const set = new Set(comp);
    const adj = new Map<string, string[]>(
      comp.map(id => [id, [...(this.partners.get(id) ?? [])].filter(p => set.has(p))] as const)
    );
    const deg = (id: string) => adj.get(id)!.length;
    const maxDeg = Math.max(...comp.map(deg));
    if (maxDeg > 2) return comp.slice().sort((a, b) => orderIdx.get(a)! - orderIdx.get(b)!);
    const endpoints = comp.filter(id => deg(id) === 1).sort((a, b) => orderIdx.get(a)! - orderIdx.get(b)!);
    const start = endpoints.length ? endpoints[0] : comp.slice().sort((a, b) => orderIdx.get(a)! - orderIdx.get(b)!)[0];
    const ordered: string[] = []; const seen = new Set<string>();
    let cur: string | undefined = start, prev: string | null = null;
    while (cur != null && !seen.has(cur)) {
      seen.add(cur); ordered.push(cur);
      const next: string | undefined = adj.get(cur)!.find(n => n !== prev && !seen.has(n));
      prev = cur; cur = next;
    }
    for (const id of comp) if (!seen.has(id)) ordered.push(id);
    if (orderIdx.get(ordered[0])! > orderIdx.get(ordered[ordered.length - 1])!) ordered.reverse();
    return ordered;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Phase 5c: Fokus-Linie exakt senkrecht ausrichten
  // ═══════════════════════════════════════════════════════════════════════════
  //
  // snapCoupleBlocks richtet jede Ebene unabhängig aus und kann das oberste
  // Spine-Paar leicht gegen die Achse verschieben. Hier wird pro Generation ein
  // einheitlicher Versatz angewandt, sodass die Paar-Symbole der direkten Linie
  // (und der Fokus selbst) exakt auf einer senkrechten Spalte liegen. Da jede
  // Ebene als Ganzes verschoben wird, entstehen KEINE neuen Überlappungen
  // innerhalb einer Ebene; Seitenzweige wandern nur minimal mit.

  private alignSpineColumn() {
    if (!this.spineActive) return;
    const focusPos = this.focusId ? this.personPositions.get(this.focusId) : undefined;
    if (!focusPos) return;
    const targetX = focusPos.x;

    // Pro Ebene den nötigen Versatz bestimmen (Spine-Paar-Symbol bzw. Fokus → targetX)
    const shiftByRank = new Map<number, number>();
    for (const cid of this.spineCouples) {
      const u = this.coupleMap.get(cid);
      if (!u) continue;
      const p1 = this.personPositions.get(u.p1Id);
      if (!p1) continue;
      const p2x = u.p2Id ? this.personPositions.get(u.p2Id)?.x : undefined;
      const symX = p2x != null ? (p1.x + p2x) / 2 : p1.x;
      shiftByRank.set(Math.round(p1.y / V_STEP), targetX - symX);
    }
    // Fokus-Ebene zusätzlich auf die Spalte ziehen
    shiftByRank.set(Math.round(focusPos.y / V_STEP), targetX - focusPos.x);

    for (const pos of this.personPositions.values()) {
      const dx = shiftByRank.get(Math.round(pos.y / V_STEP));
      if (dx) pos.x += dx;
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Phase 6: Visuelle Elemente aus finalen Positionen aufbauen
  // ═══════════════════════════════════════════════════════════════════════════

  private rebuildVisuals() {
    this.symbolPositions = [];
    this.familyLines = [];
    this.familyColorCounter = 0;

    for (const unit of this.coupleMap.values()) {
      const p1Pos = this.personPositions.get(unit.p1Id);
      if (!p1Pos) continue;

      let symbolX = p1Pos.x;
      const isCouple = !!unit.p2Id;

      // Paar-Symbol zwischen den Partnern
      if (unit.p2Id) {
        const p2Pos = this.personPositions.get(unit.p2Id);
        if (p2Pos) {
          symbolX = (p1Pos.x + p2Pos.x) / 2;
          this.symbolPositions.push({ x: symbolX, y: p1Pos.y, link: unit.link });
        }
      }

      // Eltern-Kind-Linien
      if (unit.childIds.length === 0) continue;

      const childPositions = unit.childIds
        .filter(cid => this.personPositions.has(cid))
        .map(cid => ({
          x: this.personPositions.get(cid)!.x,
          y: this.personPositions.get(cid)!.y,
        }));

      if (childPositions.length === 0) continue;

      this.familyLines.push({
        symbolX,
        parentY: p1Pos.y,
        junctionY: 0, // wird in distributeJunctions berechnet
        isCouple,
        childPositions,
        colorIndex: this.familyColorCounter++,
      });
    }

    // Querleisten gleichmäßig in den freien Lücken verteilen
    this.distributeJunctions();
  }

  /**
   * Verteilt alle Querleisten (horizontale Verbindungslinien) gleichmäßig
   * in den freien Zonen zwischen den Generationen.
   * - Gruppiert Linien nach Generationspaar (Eltern-Y → Kind-Y)
   * - Findet hindernisfreie Zonen (Karten-Abstände ≥ 25px)
   * - Verteilt Linien einer Gruppe gleichmäßig in der größten Zone
   */
  private distributeJunctions() {
    const CARD_PADDING = 25; // Mindestabstand zu Karten-Rändern

    // Linien nach Generationspaar gruppieren
    const groups = new Map<string, number[]>();
    for (let i = 0; i < this.familyLines.length; i++) {
      const line = this.familyLines[i];
      const childY = line.childPositions[0]?.y ?? line.parentY + V_STEP;
      const key = `${Math.round(line.parentY)}:${Math.round(childY)}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(i);
    }

    for (const indices of groups.values()) {
      const first = this.familyLines[indices[0]];
      const parentY = first.parentY;
      const childY = first.childPositions[0]?.y ?? parentY + V_STEP;

      const gapTop = parentY + NODE_H / 2 + CARD_PADDING;
      const gapBottom = childY - NODE_H / 2 - CARD_PADDING;

      if (gapBottom <= gapTop) {
        // Kein Platz → Mitte verwenden
        for (const idx of indices) {
          this.familyLines[idx].junctionY = (parentY + childY) / 2;
        }
        continue;
      }

      // X-Bereich aller Linien dieser Gruppe sammeln (für Hindernis-Erkennung)
      let groupMinX = Infinity, groupMaxX = -Infinity;
      for (const idx of indices) {
        const line = this.familyLines[idx];
        groupMinX = Math.min(groupMinX, line.symbolX, ...line.childPositions.map(c => c.x));
        groupMaxX = Math.max(groupMaxX, line.symbolX, ...line.childPositions.map(c => c.x));
      }

      // Hindernisse: Personen in der Lücke zwischen den Generationen
      const obstacles: Array<{ top: number; bottom: number }> = [];
      for (const pos of this.personPositions.values()) {
        const cardTop = pos.y - NODE_H / 2 - CARD_PADDING;
        const cardBottom = pos.y + NODE_H / 2 + CARD_PADDING;
        if (cardBottom > gapTop && cardTop < gapBottom &&
            pos.x + NODE_W / 2 >= groupMinX - 10 && pos.x - NODE_W / 2 <= groupMaxX + 10) {
          obstacles.push({ top: cardTop, bottom: cardBottom });
        }
      }

      // Freie Zonen berechnen
      let freeZones: Array<{ start: number; end: number }> = [{ start: gapTop, end: gapBottom }];
      for (const obs of obstacles) {
        const newZones: typeof freeZones = [];
        for (const zone of freeZones) {
          if (obs.bottom <= zone.start || obs.top >= zone.end) {
            newZones.push(zone); // Zone nicht betroffen
          } else {
            // Zone wird durch Hindernis geteilt
            if (obs.top > zone.start) newZones.push({ start: zone.start, end: obs.top });
            if (obs.bottom < zone.end) newZones.push({ start: obs.bottom, end: zone.end });
          }
        }
        freeZones = newZones;
      }

      // Größte freie Zone wählen
      freeZones.sort((a, b) => (b.end - b.start) - (a.end - a.start));
      const bestZone = freeZones[0] ?? { start: gapTop, end: gapBottom };

      // Linien nach X-Position sortieren (konsistente Reihenfolge)
      indices.sort((a, b) => this.familyLines[a].symbolX - this.familyLines[b].symbolX);

      // Gleichmäßig in der Zone verteilen
      if (indices.length === 1) {
        this.familyLines[indices[0]].junctionY = (bestZone.start + bestZone.end) / 2;
      } else {
        const step = (bestZone.end - bestZone.start) / (indices.length + 1);
        for (let i = 0; i < indices.length; i++) {
          this.familyLines[indices[i]].junctionY = bestZone.start + step * (i + 1);
        }
      }
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Phase 7: Normalisierung (Minimum auf 40 px verschieben)
  // ═══════════════════════════════════════════════════════════════════════════

  private normalize() {
    let minX = Infinity, minY = Infinity;

    for (const pos of this.personPositions.values()) {
      if (pos.x < minX) minX = pos.x;
      if (pos.y < minY) minY = pos.y;
    }
    for (const sym of this.symbolPositions) {
      if (sym.x < minX) minX = sym.x;
      if (sym.y < minY) minY = sym.y;
    }

    if (minX === Infinity) return;

    const dx = -minX + 40;
    const dy = -minY + 40;

    for (const pos of this.personPositions.values()) {
      pos.x += dx;
      pos.y += dy;
    }
    for (const sym of this.symbolPositions) {
      sym.x += dx;
      sym.y += dy;
    }
    for (const line of this.familyLines) {
      line.symbolX += dx;
      line.parentY += dy;
      line.junctionY += dy;
      for (const cp of line.childPositions) {
        cp.x += dx;
        cp.y += dy;
      }
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Ergebnisse sammeln
  // ═══════════════════════════════════════════════════════════════════════════

  private collectResults(): FamilyTreeLayoutResult {
    return {
      layoutPersons: this.data.nodes
        .filter(n => this.personPositions.has(n.id))
        .map(n => ({
          x: this.personPositions.get(n.id)!.x,
          y: this.personPositions.get(n.id)!.y,
          data: n,
        })),
      layoutSymbols: this.symbolPositions,
      layoutLines: this.familyLines,
    };
  }

  private makeCoupleId(p1: string, p2: string | null): string {
    if (!p2) return `single:${p1}`;
    return [p1, p2].sort().join(':');
  }
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useFamilyTreeLayout(
  treeData: TreeData | undefined,
  focusId: string | null = null,
): FamilyTreeLayoutResult {
  return useMemo(() => {
    if (!treeData?.nodes?.length) {
      return { layoutPersons: [], layoutSymbols: [], layoutLines: [] };
    }
    try {
      return new FamilyTreeLayoutEngine(treeData, focusId).compute();
    } catch {
      return {
        layoutPersons: treeData.nodes.map((n, i) => ({
          x: i * (NODE_W + 40), y: 0, data: n,
        })),
        layoutSymbols: [],
        layoutLines: [],
      };
    }
  }, [treeData, focusId]);
}

export { NODE_W, NODE_H, SYMBOL_W };
